import { useQuery } from "@apollo/client";
import React, { useContext, useState } from "react";
import { Button } from "react-bootstrap";
import { GET_ALL_TEAMS_AND_USERS } from "../../graphql/query";
import { createInitials } from "../../utils/functions/createInitials";
import ReactModal from "react-modal";
import TransferChatConfirmModal from "./TransferChatConfirmModal";
import UserContext from "../../context/UserContext";

interface IProps {
  closeTransferModal: () => void;
  chatRoomID: number;
  setUpdateChatRoomsFlag: any;
  chatRoomsArrayRef: any;
}

const TransferChatModal: React.FC<IProps> = ({
  closeTransferModal,
  chatRoomID,
  setUpdateChatRoomsFlag,
  chatRoomsArrayRef,
}) => {
  const [isMainModalOpen, setIsMainModalOpen] = useState(true);
  const [isTransferConfirmModalOpen, setIsTransferConfirmModalOpen] =
    useState(false);
  const [selectedUser, setSelectedUser] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<string>("");
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const { currentUser } = useContext(UserContext);

  const customModalStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      borderRadius: "8px",
      border: "none",
      boxShadow: "0 24px 24px rgba(0,0,0,0.1)",
      padding: "0",
      width: "400px",
      height: "400px",
      backgroundColor: "white",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
  };

  const transferConfirmModalStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      borderRadius: "15px",
      border: "none",
      boxShadow: "0 2px 8px 0 #00000029",
    },
  };

  const { data, error, loading } = useQuery(GET_ALL_TEAMS_AND_USERS, {
    variables: {
      input: {
        teamName: null,
        memberName: null,
      },
    },
    context: {
      headers: {
        "access-control-allow-methods": "Human-hand-off",
      },
    },
  });

  const getFilteredUsers = () => {
    if (!selectedTeam || !data?.getAllTeamsAndUsers?.data) return [];

    const team = data.getAllTeamsAndUsers.data.find(
      (t: any) => t.id === parseInt(selectedTeam)
    );
    if (!team) return [];

    return team.users.filter(
      (user: any) => parseInt(currentUser.id) !== user.id
    );
  };

  const filteredUsers = getFilteredUsers();

  const handleTransferClick = () => {
    const user = filteredUsers.find(
      (u: any) => u.id === parseInt(selectedUserId)
    );
    if (user) {
      setSelectedUser({ id: user.id, name: user.name });
      setIsTransferConfirmModalOpen(true);
      setIsMainModalOpen(false); // Close main modal when confirm modal opens
    }
  };

  const closeMainModal = () => {
    setIsMainModalOpen(false);
    closeTransferModal(); // Call prop function to notify parent if needed
  };

  const closeTransferConfirmModal = () => {
    setIsTransferConfirmModalOpen(false);
    setSelectedUser(null);
  };

  return (
    <>
      <ReactModal
        isOpen={isMainModalOpen}
        onRequestClose={closeMainModal}
        style={customModalStyles}
        ariaHideApp={false}
      >
        <div className="h-100 d-flex flex-column">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center border-bottom px-4 py-3">
            <h5 className="fw-semibold mb-0">Transfer Chat</h5>
            <button
              onClick={closeMainModal}
              className="btn btn-sm btn-light rounded-circle d-flex align-items-center justify-content-center"
              style={{ width: "36px", height: "36px" }}
            >
              <i className="bi bi-x-lg fs-6"></i>
            </button>
          </div>

          {/* Content */}
          <div className="flex-grow-1 px-4 py-3 overflow-auto">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status" />
              </div>
            ) : error ? (
              <div className="alert alert-danger mt-3">
                Error loading teams and users. Please try again.
              </div>
            ) : (
              <>
                {/* Team Selector */}
                <div className="mb-4">
                  <label className="form-label fw-semibold">Select Team</label>
                  <select
                    className="form-select rounded"
                    value={selectedTeam}
                    onChange={(e) => {
                      setSelectedTeam(e.target.value);
                      setSelectedUserId("");
                    }}
                  >
                    <option value="" disabled>
                      Select a team
                    </option>
                    {data?.getAllTeamsAndUsers?.data?.map((team: any) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* User Selector + Preview - Only if team selected */}
                {selectedTeam && (
                  <>
                    <div className="mb-4">
                      <label className="form-label fw-semibold">
                        Select User
                      </label>
                      <select
                        className="form-select rounded"
                        value={selectedUserId}
                        onChange={(e) => setSelectedUserId(e.target.value)}
                      >
                        <option value="" disabled>
                          Select a user
                        </option>
                        {filteredUsers.map((user: any) => (
                          <option key={user.id} value={user.id}>
                            {user.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Selected User Preview */}
                    {selectedUserId && (
                      <div className="mb-4 p-3 bg-light rounded d-flex align-items-center">
                        {(() => {
                          const user = filteredUsers.find(
                            (u: any) => u.id === parseInt(selectedUserId)
                          );
                          if (!user) return null;
                          const { label, state } = createInitials(user.name);
                          return (
                            <>
                              <div className="symbol symbol-40px symbol-circle me-3">
                                <span
                                  className={`symbol-label bg-light-${state} text-${state} fs-6 fw-bolder`}
                                >
                                  {label}
                                </span>
                              </div>
                              <div>
                                <div className="fw-semibold">{user.name}</div>
                                <div className="text-muted small">
                                  {user.email}
                                </div>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="border-top px-4 py-3">
            <Button
              className="btn btn-primary w-100"
              onClick={handleTransferClick}
              disabled={!selectedTeam || !selectedUserId}
            >
              Transfer Chat
            </Button>
          </div>
        </div>
      </ReactModal>

      {/* Transfer Confirmation Modal */}
      {selectedUser && (
        <ReactModal
          isOpen={isTransferConfirmModalOpen}
          onRequestClose={closeTransferConfirmModal}
          style={transferConfirmModalStyles}
          contentLabel="Transfer chat confirmation"
          ariaHideApp={false}
        >
          <TransferChatConfirmModal
            closeTransferConfirmModal={closeTransferConfirmModal}
            closeTransferModal={closeTransferModal}
            userId={selectedUser.id}
            userName={selectedUser.name}
            chatRoomID={chatRoomID}
            setUpdateChatRoomsFlag={setUpdateChatRoomsFlag}
            chatRoomsArrayRef={chatRoomsArrayRef}
            closeMainModal={closeMainModal}
          />
        </ReactModal>
      )}
    </>
  );
};

export default TransferChatModal;
